import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, LogIn, LogOut, BookOpen, ChevronDown, Settings, Eye, MessageCircle, Notebook, Home, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const ROLE_LABELS = {
  guru: 'Guru',
  ortu: 'Orang Tua',
  umum: 'Umum',
  moderator: 'Moderator',
  admin: 'Admin',
};

const Navbar = ({ onLoginClick, onRegisterClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleLogin = () => {
    if (onLoginClick) {
      onLoginClick();
    } else {
      navigate('/?login=true');
    }
  };

  const handleRegister = () => {
    if (onRegisterClick) {
      onRegisterClick();
    } else {
      navigate('/?register=true');
    }
  };

  const isAdmin = user?.role === 'admin';
  const isModerator = user?.role === 'moderator';
  const isLearner = ['guru', 'ortu', 'umum'].includes(user?.role);
  // Navigation items for authenticated users
  const navItems = [
    { path: '/mulai', label: 'Mulai', icon: Home, description: null },
    { path: '/belajar', label: 'Belajar', icon: BookOpen, description: 'Mulai atau lanjutkan jalur kesadaran Anda.' },
    { path: '/refleksi', label: 'Refleksi', icon: Notebook, description: 'Tuliskan apa yang Anda sadari hari ini.' },
    { path: '/dialog', label: 'Dialog', icon: MessageCircle, description: 'Berbincang setelah membaca dan merenung.' },
    { path: '/donasi', label: 'Donasi', icon: Heart, description: 'Jika perjalanan ini bermakna, Anda bisa mendukung keberlanjutannya.' },
    { path: '/profil', label: 'Profil', icon: User, description: null },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled
        ? 'bg-white shadow-lg border-b border-gray-100'
        : 'bg-white/80 backdrop-blur-md'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(isAuthenticated ? '/mulai' : '/')}>
            <img
              src="/logo-af.png"
              alt="Akademia Fitrah"
              className="w-10 h-10 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-500 leading-tight">Akademia</span>
              <span className="text-xl font-bold text-blue-700 -mt-0.5">FITRAH</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {isAuthenticated ? (
              // Authenticated: Mulai | Belajar | Refleksi | Dialog | Profil
              <TooltipProvider delayDuration={300}>
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const btn = (
                    <button
                      onClick={() => navigate(item.path)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive(item.path)
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </button>
                  );
                  if (item.description) {
                    return (
                      <Tooltip key={item.path}>
                        <TooltipTrigger asChild>{btn}</TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-gray-900 text-white text-xs max-w-xs px-3 py-2 rounded-lg">
                          {item.description}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }
                  return <React.Fragment key={item.path}>{btn}</React.Fragment>;
                })}
              </TooltipProvider>
            ) : (
              // Not authenticated: orientasi proses belajar (bukan company profile)
              <>
                <a href="/#perjalanan" className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium px-3 py-2">Peta Perjalanan</a>
                <a href="/#mulai-belajar" className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium px-3 py-2">Mulai Perjalanan</a>
              </>
            )}
          </div>

          {/* Auth Buttons / User Menu */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-gray-700 hover:text-blue-600 hover:bg-blue-50">
                    {user?.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover mr-2"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div
                      className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 items-center justify-center text-white font-bold mr-2"
                      style={{ display: user?.avatar_url ? 'none' : 'flex' }}
                    >
                      {(user?.display_name || user?.name)?.charAt(0).toUpperCase()}
                    </div>
                    {(user?.display_name || user?.name)?.split(' ')[0]}
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-gray-900">{user?.name}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {ROLE_LABELS[user?.role] || user?.role}
                    </span>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/mulai')}>
                    <Eye className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/profil')}>
                    <User className="w-4 h-4 mr-2" />
                    Profil
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/admin')}>
                      <Settings className="w-4 h-4 mr-2" />
                      Dashboard Admin
                    </DropdownMenuItem>
                  )}
                  {!isAdmin && isModerator && (
                    <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/moderator/dashboard')}>
                      <Settings className="w-4 h-4 mr-2" />
                      Panel Moderator
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer text-red-600" onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Keluar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="text-gray-600 hover:text-blue-600 hover:bg-blue-50"
                  onClick={handleLogin}
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Masuk
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md shadow-blue-600/30"
                  onClick={handleRegister}
                >
                  Mulai Perjalanan
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-700"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {isAuthenticated ? (
              <>
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); setIsMenuOpen(false); }}
                      className={`flex items-center gap-2.5 w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium ${
                        isActive(item.path)
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <div>
                        <span>{item.label}</span>
                        {item.description && (
                          <span className="block text-xs text-gray-400 font-normal mt-0.5">{item.description}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
                <div className="pt-3 border-t border-gray-100">
                  <div className="py-2 px-3">
                    <p className="font-medium text-gray-900">{user?.name}</p>
                    <p className="text-sm text-gray-500">{ROLE_LABELS[user?.role] || user?.role}</p>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-gray-600"
                      onClick={() => { navigate('/admin'); setIsMenuOpen(false); }}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Dashboard Admin
                    </Button>
                  )}
                  {!isAdmin && isModerator && (
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-gray-600"
                      onClick={() => { navigate('/moderator/dashboard'); setIsMenuOpen(false); }}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Panel Moderator
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-red-600"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Keluar
                  </Button>
                </div>
              </>
            ) : (
              <>
                <a href="/#perjalanan" className="block text-gray-600 hover:text-blue-600 py-2 px-3">Peta Perjalanan</a>
                <a href="/#mulai-belajar" className="block text-gray-600 hover:text-blue-600 py-2 px-3">Mulai Perjalanan</a>
                <div className="pt-3 border-t border-gray-100 space-y-2">
                  <Button variant="ghost" className="w-full justify-start" onClick={handleLogin}>
                    <LogIn className="w-4 h-4 mr-2" />
                    Masuk
                  </Button>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleRegister}>
                    Mulai Perjalanan
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
